<template>
  <div class="flex flex-col">
    <div
      class="mb-4 flex flex-wrap items-center justify-between"
    >
      <h2 class="text-lg font-medium text-background-fg/87">
        {{ chartLabel }}
      </h2>
      <div
        v-if="!isLoading && !isError"
        class="flex flex-wrap space-x-2"
      >
        <InputSelect
          v-if="props.source !== 'beepaidgo'"
          v-model="selectedCurrency"
          :options="currencyOptions"
        />

        <WeeklyTransactionSummaryTypeToggle
          v-model="dataType"
        />
      </div>
    </div>

    <QueryLoading v-if="isLoading" />
    <QueryError
      v-else-if="isError"
      class="min-h-64"
      :show-retry="true"
      @retry="refetch"
    >
      Failed to retrieve weekly transaction summary
    </QueryError>
    <WeeklyTransactionSummaryChart
      v-else-if="summaryForSelectedCurrency"
      :data-type="dataType"
      :data="summaryForSelectedCurrency"
      :currency="selectedCurrency"
    />

    <div
      v-if="calculatedOn"
      class="mr-2 mt-2 self-end text-xs text-black/30"
    >
      calculated at {{ calculatedOn }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { WeeklyTransactionSummaryType } from "./WeeklyTransactionSummaryTypeToggle.vue"
import type { MerchantCurrencyCode } from "@/utils/currency"
import { format, formatISO } from "date-fns"
import { ref, computed } from "vue"

const props = defineProps<{
  source: PlatformStatsSources
}>()

const { data, isLoading, isError, refetch } =
  props.source === "tpplus"
    ? useWeeklyTransactionChartDataTpplus()
    : useWeeklyTransactionChartDataBeepaidGo()

const currentWeek = formatISO(new Date(), {
  representation: "date",
})

const { defaultMerchantCurrency } = useOrganizationConfig()
const selectedCurrencyOverride =
  ref<MerchantCurrencyCode | null>(null)
const selectedCurrency = computed<MerchantCurrencyCode>({
  get: () =>
    selectedCurrencyOverride.value ??
    defaultMerchantCurrency.value,
  set: (newValue) => {
    selectedCurrencyOverride.value = newValue
  },
})

const summaryForSelectedCurrency = computed(() => {
  if (!data.value?.data) {
    return null
  }

  const currencyData =
    data.value.data[selectedCurrency.value]

  if (!currencyData) {
    return null
  }

  return currencyData.filter(
    (row) => row.weekStart !== currentWeek
  )
})

const calculatedOn = computed(
  () =>
    data.value &&
    data.value.generatedAt &&
    format(data.value.generatedAt, "HH:mm dd/MM/yyyy")
)

const currencyOptions = merchantCurrencyCodeOptions

const dataType =
  ref<WeeklyTransactionSummaryType>("cardTurnover")

const chartLabel = computed(() =>
  dataType.value === "cardTurnover"
    ? "Card Turnover By Week"
    : dataType.value === "activeMerchantCount"
      ? "Active Merchant Count By Week"
      : "Transaction Count By Week"
)
</script>
